import { useCallback, useEffect, useState } from 'react';
import { useLearning } from 'src/hook/data/useLearning';
import { LanguageLevels } from 'src/model/LanguageLevels';
import { Word } from 'src/model/Word';
import useUser from '../data/useUser';
import { useWords } from '../data/useWords';

interface Learning {
    word: Word;
    everLearnt: () => boolean;
}

const useHardWord = () => {
    const { userInfo } = useUser();
    const { learningInfo } = useLearning();
    const [hardWordThreshold, setHardWordThreshold] = useState<number | null>(null);
    const { getWordsByWordList } = useWords();

    const calculateHardWordThreshold = useCallback(() => {
        if (!userInfo || !learningInfo) return 0;

        const toLearnWords = getWordsByWordList(learningInfo.ToLearn || []);

        const learnings = toLearnWords.map((word: Word) => ({
            word,
            everLearnt: () => learningInfo.ReviewWordIds?.includes(word.ID.toString()) || false
        }));

        const filteredLearnings = learnings.filter((learning: Learning) => !learning.everLearnt());
        const sortedLearnings = filteredLearnings.sort((a: Learning, b: Learning) => a.word.Rank - b.word.Rank);
        const top30Learnings = sortedLearnings.slice(0, 30);

        const averageRank = top30Learnings.length > 0
            ? top30Learnings.reduce((sum: number, learning: Learning) => sum + learning.word.Rank, 0) / top30Learnings.length
            : 0;

        return averageRank;
    }, [userInfo, learningInfo, getWordsByWordList]);

    useEffect(() => {
        const baseThreshold = calculateHardWordThreshold();
        let finalThreshold = baseThreshold;

        if (userInfo?.LanguageLevel) {
            const levelRatio = LanguageLevels[userInfo.LanguageLevel as keyof typeof LanguageLevels].Ratio;
            finalThreshold = Math.max(baseThreshold, levelRatio * 8000);
        }

        setHardWordThreshold(finalThreshold);
    }, [userInfo, calculateHardWordThreshold]);

    const isTooHard = useCallback((word: Word): boolean => {
        if (hardWordThreshold === null || hardWordThreshold === 0) return false;
        return word.Rank > hardWordThreshold + 2500;
    }, [hardWordThreshold]);

    return {
        isTooHard,
    };
};

export default useHardWord;
